import { Alert, Box, Typography } from '@mui/material';
import { useMemo } from 'react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import palette from 'src/theme/palette';

import type { RetentionDecayPoint } from './types';

interface PurchaseDecayChartProps {
  data: RetentionDecayPoint[];
}

/** Riga grafico: punto API + metadati finestra per tooltip. */
type DecayChartRow = RetentionDecayPoint & {
  windowStartDay: number | null;
};

type DecayTooltipProps = {
  active?: boolean;
  payload?: { payload: DecayChartRow }[];
  label?: string | number;
};

function PurchaseDecayTooltip({ active, payload, label }: DecayTooltipProps) {
  if (!active || !payload?.[0]?.payload) return null;
  const row = payload[0].payload;
  const cumPct = Math.round(Number(row.repurchasedPercentage) * 100);
  const incPct = Math.round(Number(row.incrementalRepurchasedPercentage) * 100);
  const day = typeof label === 'number' ? label : Number(label);
  const windowCopy =
    row.windowStartDay === null
      ? 'Same calendar day as first order (0 d)'
      : `Between day ${row.windowStartDay + 1} and day ${row.day}`;

  return (
    <Box
      sx={{
        bgcolor: palette.common.white,
        border: `1px solid ${palette.grey[200]}`,
        borderRadius: 1.5,
        px: 1.35,
        py: 1,
        boxShadow: '0 10px 28px -14px rgba(15, 23, 42, 0.35)',
        minWidth: 200,
      }}
    >
      <Typography
        sx={{
          fontWeight: 800,
          fontSize: '0.875rem',
          color: palette.text.primary,
          lineHeight: 1.2,
        }}
      >
        {Number.isFinite(day) ? `By day ${day}` : ''}
      </Typography>
      <Typography
        sx={{
          mt: 0.45,
          fontSize: '0.7rem',
          lineHeight: 1.35,
          color: palette.text.secondary,
        }}
      >
        {windowCopy}
      </Typography>
      <Typography
        sx={{
          mt: 0.65,
          fontSize: '0.75rem',
          lineHeight: 1.4,
          color: palette.text.secondary,
        }}
      >
        <Box
          component="span"
          sx={{ fontWeight: 700, color: palette.text.primary }}
        >
          {cumPct}%
        </Box>{' '}
        cumulative second order
      </Typography>
      <Typography
        sx={{
          mt: 0.35,
          fontSize: '0.75rem',
          lineHeight: 1.4,
          color: palette.text.secondary,
        }}
      >
        <Box
          component="span"
          sx={{ fontWeight: 700, color: palette.text.primary }}
        >
          {incPct}%
        </Box>{' '}
        new in this window (
        {row.incrementalRepurchasedCustomers.toLocaleString()} ·{' '}
        {row.eligibleCustomers.toLocaleString()} eligible)
      </Typography>
      <Typography
        sx={{
          mt: 0.35,
          fontSize: '0.7rem',
          lineHeight: 1.35,
          color: palette.text.secondary,
        }}
      >
        {row.repurchasedCustomers.toLocaleString()} repurchased cumulatively
      </Typography>
    </Box>
  );
}

export function PurchaseDecayChart({ data }: PurchaseDecayChartProps) {
  const chartRows: DecayChartRow[] = useMemo(() => {
    const sorted = [...data].sort((a, b) => a.day - b.day);
    return sorted.map((p, i) => ({
      ...p,
      windowStartDay: i === 0 ? null : sorted[i - 1]!.day,
    }));
  }, [data]);

  const { maxRateAt180, winbackThresholdDay, yAxisMax } = useMemo(() => {
    if (chartRows.length === 0) {
      return {
        maxRateAt180: 0,
        winbackThresholdDay: null as number | null,
        yAxisMax: 1,
      };
    }
    const at180 =
      chartRows.find((p) => p.day === 180) ?? chartRows[chartRows.length - 1];
    const maxRateAt180Val = Number(at180?.repurchasedPercentage ?? 0);
    const target = maxRateAt180Val * 0.85;
    let winbackDay: number | null = null;
    if (maxRateAt180Val > 0) {
      const hit = chartRows.find((p) => p.repurchasedPercentage >= target);
      winbackDay = hit?.day ?? null;
    }
    const peakCum = Math.max(
      ...chartRows.map((p) => p.repurchasedPercentage),
      0,
    );
    const peakInc = Math.max(
      ...chartRows.map((p) => p.incrementalRepurchasedPercentage),
      0,
    );
    const peak = Math.max(peakCum, peakInc);
    const yAxisMaxVal = Math.min(
      1,
      Math.max(0.1, Math.ceil(peak * 20) / 20 + 0.02),
    );

    return {
      maxRateAt180: maxRateAt180Val,
      winbackThresholdDay: winbackDay,
      yAxisMax: yAxisMaxVal,
    };
  }, [chartRows]);

  const summaryLine = useMemo(() => {
    if (chartRows.length === 0) return '';
    const pct180 = Math.round(maxRateAt180 * 100);
    if (winbackThresholdDay !== null && maxRateAt180 > 0) {
      return `${pct180}% of first-time buyers in this slice placed a second order within 180 days (among shoppers with enough history on file). About 85% of that gain shows up by day ${winbackThresholdDay}.`;
    }
    return `${pct180}% of first-time buyers in this slice placed a second order within 180 days (among shoppers with enough history on file).`;
  }, [chartRows, maxRateAt180, winbackThresholdDay]);

  if (chartRows.length === 0) {
    return (
      <Typography sx={{ color: palette.text.secondary, fontSize: '0.8125rem' }}>
        Not enough cohort data to plot second-purchase timing.
      </Typography>
    );
  }

  return (
    <Box
      className="retention-pdf-chart-fit"
      sx={{
        width: '100%',
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 0,
        pb: 0.25,
      }}
    >
      {maxRateAt180 < 0.1 ? (
        <Alert
          severity="warning"
          sx={{ mb: 1.25, borderRadius: 1.5, fontSize: '0.78rem' }}
        >
          Low repeat in this window: second orders are rare—tighten lifecycle
          tests or widen the date range before leaning on this curve for big
          bets.
        </Alert>
      ) : null}

      <Box
        display="flex"
        flexDirection="column"
        gap={0.65}
        sx={{ flexWrap: 'wrap', mb: 1 }}
      >
        <Box
          display="flex"
          alignItems="center"
          gap={0.85}
          sx={{ flexWrap: 'wrap' }}
        >
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: 0.5,
              bgcolor: palette.primary.main,
              flexShrink: 0,
            }}
          />
          <Typography
            sx={{
              fontSize: '0.75rem',
              fontWeight: 600,
              color: palette.text.secondary,
              letterSpacing: '0.01em',
            }}
          >
            Cumulative % who placed order #2
          </Typography>
        </Box>
        <Box
          display="flex"
          alignItems="center"
          gap={0.85}
          sx={{ flexWrap: 'wrap' }}
        >
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: 0.5,
              bgcolor: 'rgba(30, 237, 159, 0.55)',
              flexShrink: 0,
            }}
          />
          <Typography
            sx={{
              fontSize: '0.75rem',
              fontWeight: 600,
              color: palette.text.secondary,
              letterSpacing: '0.01em',
            }}
          >
            New second orders in each window (share of same eligible cohort)
          </Typography>
        </Box>
      </Box>

      <Box
        className="retention-pdf-chart-canvas"
        sx={{ width: '100%', height: 280, flexShrink: 0 }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={chartRows}
            margin={{ top: 8, right: 12, left: 4, bottom: 36 }}
          >
            <CartesianGrid vertical={false} stroke={palette.grey[200]} />
            <XAxis
              dataKey="day"
              tick={{ fill: palette.text.secondary, fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              height={32}
              label={{
                value: 'Days since first purchase (bucket end)',
                position: 'bottom',
                offset: 18,
                fill: palette.text.secondary,
                fontSize: 11,
                fontWeight: 600,
              }}
            />
            <YAxis
              domain={[0, yAxisMax]}
              tickCount={6}
              tickFormatter={(value) => `${Math.round(Number(value) * 100)}%`}
              tick={{ fill: palette.text.secondary, fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              width={44}
            />
            <Tooltip
              content={<PurchaseDecayTooltip />}
              cursor={{ fill: 'rgba(148, 163, 184, 0.08)' }}
            />
            {winbackThresholdDay !== null ? (
              <ReferenceLine
                x={winbackThresholdDay}
                stroke="#94a3b8"
                strokeDasharray="4 4"
                strokeWidth={1}
                label={{
                  value: 'Win-back hint',
                  position: 'top',
                  fill: palette.text.secondary,
                  fontSize: 10,
                  fontWeight: 600,
                }}
              />
            ) : null}
            <Bar
              dataKey="incrementalRepurchasedPercentage"
              name="New in window"
              fill="rgba(30, 237, 159, 0.55)"
              maxBarSize={36}
              radius={[4, 4, 0, 0]}
              isAnimationActive={false}
            />
            <Line
              type="stepAfter"
              dataKey="repurchasedPercentage"
              name="Cumulative"
              stroke={palette.primary.main}
              strokeWidth={2.5}
              dot={{ r: 3, fill: palette.primary.main, strokeWidth: 0 }}
              isAnimationActive={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </Box>

      {summaryLine ? (
        <Typography
          sx={{
            mt: 1,
            fontSize: '0.8125rem',
            lineHeight: 1.45,
            color: palette.text.primary,
            fontWeight: 600,
          }}
        >
          {summaryLine}
        </Typography>
      ) : null}

      <Typography
        variant="caption"
        sx={{
          color: palette.text.secondary,
          mt: summaryLine ? 0.85 : 1.15,
          display: 'block',
          fontSize: '0.75rem',
          lineHeight: 1.45,
        }}
      >
        Bars: share of the eligible cohort whose second order falls in each
        window between bucket cuts. Line: cumulative second-order rate at each
        cut. Eligible counts only customers with at least that many days between
        their first order and the end of your selected range, so very recent
        first orders do not deflate the rate.
      </Typography>
    </Box>
  );
}

export default PurchaseDecayChart;
